import { IsArray, IsBoolean, IsEnum, IsNumber, IsObject, IsOptional, IsString, Matches, MaxLength, ValidateNested } from 'class-validator';
import { Type } from 'class-transformer';
import { AnimationConfigDto } from '../animation.dto';
import {
	CustomizationDto,
	CustomizationItemDto,
	CustomizationTypeArray
} from '../customization/customization.dto';
import { ThemeDto } from '../theme.dto';
import { LayerDto } from '../layer/layer.dto';
import { StylesConfigDto } from '../styles.dto';
import { PrintConfigDto } from '../print.dto';
import { AssetDto } from '../../../asset/models/asset.dto';
import { LocaleDto } from '../locale.dto';
import { Validation } from '../../../_core/utils/utils.validation';
import { InteractivityDto } from '../interactivity.dto';
import { ExportCropBoxDto } from './export-crop-box.dto';
import { IsValidExportCropBox } from '../../validators/export-crop-box.validator';
import { HtmlConfigDto } from '../html-config.dto';
import { ExportType } from "../../../encode/models/job-def.dto";
import { ScriptDto } from '../dto/script.dto';

export class BackgroundDto {
	@IsOptional()
	@IsString()
	assetPath?: string;

	@IsOptional()
	@IsString()
	color?: string;
}

export enum CreativeUnitDimensionsUnit {
	PIXELS = 'px',
	INCHES = 'in',
	CENTIMETERS = 'cm',
	MILLIMETERS = 'mm',
	POINTS = 'pt',
	PICAS = 'pc'
}

export class CreativeUnitDimensionsDto {
	@IsNumber()
	width: number;

	@IsNumber()
	height: number;

	/**
	 * Note: not specifically PPI, but rather a generic resolution value.
	 * ex. If the defaultDisplayUnit is:
	 * - in, it's PPI/dots per inch
	 * - cm, it's DPCm/dots per cm
	 * - mm, it's DPMm/dots per mm
	 * - pt, it's DPP/dots per point
	 * - pc, it's DPPi/dots per pica
	 */
	@IsOptional()
	@IsNumber()
	ppi?: number;

	@IsOptional()
	@IsEnum(CreativeUnitDimensionsUnit)
	defaultDisplayUnit?: CreativeUnitDimensionsUnit; // Default display unit for the dimensions.  Defaults to px.

	@IsOptional()
	@Type(() => CreativeUnitDimensionsDto)
	originalDimensions?: CreativeUnitDimensionsDto;

	@IsOptional()
	@IsArray()
	@IsOptional()
	@IsString({ each: true })
	canTransformInto?: string[]; // Other units this one can transform into.

	@Matches(Validation.numberOrPercentRgx, { message: 'Aspect Max must be a number or percent.' })
	@IsOptional()
	aspectMin?: string;

	@Matches(Validation.numberOrPercentRgx, { message: 'Aspect Max must be a number or percent.' })
	@IsOptional()
	aspectMax?: string;
}

export class CreativeUnitDto {
	@IsString()
	id: string;

	@IsString()
	name: string;

	@IsOptional()
	@IsString()
	description?: string;

	@IsString()
	packageId: string;

	@IsOptional()
	@IsString()
	listingId?: string; // Trace if this unit came from a listing.

	@IsOptional()
	group?: {
		id: string;
		name: string;
		instanceId: string;
	}

	@IsOptional()
	@IsString()
	creativeUnitId?: string; // If this creative unit is a clone of another, this is the ID of the original.

	@IsOptional()
	@IsString()
	containerClasses?: string;

	@IsOptional()
	@IsBoolean()
	appendContainerClasses?: boolean;

	@IsOptional()
	@ValidateNested({ each: true })
	@IsArray()
	@Type(() => LayerDto)
	layers: LayerDto[];

	@ValidateNested()
	@IsObject()
	@Type(() => CreativeUnitDimensionsDto)
	dimensions: CreativeUnitDimensionsDto;

	@IsOptional()
	styles?: any | StylesConfigDto[];

	@IsOptional()
	thumbnail?: AssetDto;

	@IsOptional()
	@ValidateNested()
	@Type(() => ThemeDto)
	theme?: ThemeDto;

	@IsOptional()
	@ValidateNested()
	@Type(() => InteractivityDto)
	interactivity?: InteractivityDto;

	@IsOptional()
	@IsArray()
	exportTypes?: ExportType[];

	@IsOptional()
	@ValidateNested()
	@Type(() => AnimationConfigDto)
	animationConfig?: AnimationConfigDto;

	@IsOptional()
	@ValidateNested()
	@Type(() => PrintConfigDto)
	printConfig?: PrintConfigDto;

	@IsOptional()
	@ValidateNested()
	@Type(() => BackgroundDto)
	background?: BackgroundDto;

	@IsOptional()
	@IsArray()
	customizations?: any;

	@IsOptional()
	@IsObject()
	variables?: any;

	@IsOptional()
	@IsObject()
	variableOverrides?: any; // Overrides for variables set in the creative unit.

	@IsOptional()
	@IsBoolean()
	isDefault?: boolean;

	@IsOptional()
	@IsBoolean()
	hidden?: boolean;

	@IsOptional()
	hideSize?: boolean; // Hide the size caption on the add dialog.

	@IsOptional()
	@IsString()
	templateCreativeUnit?: string;

	@IsOptional()
	@IsObject()
	metadata?: { [key: string]: string };

	@IsOptional()
	locale?: LocaleDto;

	@IsOptional()
	@IsArray()
	scripts?: ScriptDto[];

	@IsOptional()
	position?: number;

	@IsOptional()
	@IsString()
	remoteId?: string; // Used if created by external sources (ex. Figma)

	@IsOptional()
	@IsObject()
	validationErrors?: { [key: string]: string };

	@IsOptional()
	@ValidateNested()
	@Type(() => ExportCropBoxDto)
	@IsValidExportCropBox()
	exportCropBox?: ExportCropBoxDto;

	@IsOptional()
	@ValidateNested()
	@Type(() => HtmlConfigDto)
	htmlConfig?: HtmlConfigDto;

	@IsOptional()
	@IsString()
	@MaxLength(500)
	aiInstructions?: string;
}

export class CreativeUnitWithCustomizationsDto extends CreativeUnitDto {
	@ValidateNested({ each: true })
	@IsArray()
	@IsOptional()
	@Type(() => CustomizationDto, {
		keepDiscriminatorProperty: true,
		discriminator: {
			property: 'type',
			subTypes: CustomizationTypeArray
		}
	})
	customizations?: CustomizationItemDto[];

	/** Support some units having child units.  Functions as a makeshift "group". */
	items?: CreativeUnitWithCustomizationsDto[];

	@IsOptional()
	@IsObject()
	variables?: { [key: string]: any };
}
